import { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Card,
  Space,
  Alert,
  Divider,
} from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { TextArea } = Input;

const defaultConsultationOptions = {
  projectTypes: [
    "Full Home Renovation",
    "Kitchen Remodel",
    "Bathroom Remodel",
    "Commercial Space",
  ],
  budgets: ["Under $50,000", "$50k - $100k", "$100k+"],
  timelines: ["ASAP", "1-3 Months", "3-6 Months"],
};

const Contact = ({ content }) => {
  const [form] = Form.useForm();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const metadata = content?.metadata || {};
  const projectTypes = metadata.consultation_project_types?.length
    ? metadata.consultation_project_types
    : defaultConsultationOptions.projectTypes;
  const budgets = metadata.consultation_budgets?.length
    ? metadata.consultation_budgets
    : defaultConsultationOptions.budgets;
  const timelines = metadata.consultation_timelines?.length
    ? metadata.consultation_timelines
    : defaultConsultationOptions.timelines;

  // 🔥 CHANGE THIS TO YOUR WHATSAPP NUMBER
  // Format: CountryCode + Number
  // Example Malaysia: 60123456789
  const whatsappNumber = metadata.whatsapp || "60183131313";

  const handleSubmit = (values) => {
    const { name, email, phone, projectType, budget, timeline, message } =
      values;

    const textMessage = `
  NEW CONSULTATION REQUEST

  Name: ${name}
  Email: ${email}
  Phone: ${phone || "N/A"}
  Project Type: ${projectType}
  Budget: ${budget || "N/A"}
  Timeline: ${timeline || "N/A"}

  Project Details:
  ${message}
    `;

    const encodedMessage = encodeURIComponent(textMessage);

    const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    window.open(whatsappLink, "_blank");

    setIsSubmitted(true);
    form.resetFields();
  };

  return (
    <div
      id="contact"
      className="content-section contact-section"
      style={{ padding: "80px 50px", background: "#f5f5f5" }}
    >
      {/* Section Header */}
      <div style={{ textAlign: "center", marginBottom: 60 }}>
        <Title>{content?.title || "Start Your Transformation"}</Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          {content?.subtitle ||
            "Ready to revolutionize your space? Get in touch with us for a free consultation and let's bring your vision to life."}
        </Text>
      </div>

      {content?.image_url && (
        <div style={{ maxWidth: 1100, margin: "0 auto 48px" }}>
          <img
            src={content.image_url}
            alt={content.title || "Contact us"}
            loading="lazy"
            style={{
              display: "block",
              width: "100%",
              maxHeight: 440,
              objectFit: "cover",
              borderRadius: 16,
            }}
          />
        </div>
      )}

      <Row gutter={[60, 40]}>
        {/* LEFT SIDE */}
        <Col xs={24} lg={12}>
          <Title level={3}>Get in Touch</Title>

          <Space orientation="vertical" size="large" style={{ marginTop: 30 }}>
            <Space align="start">
              <PhoneOutlined style={{ fontSize: 22, color: "#faad14" }} />
              <div>
                <Text strong>Phone</Text>
                <br />
                <Text type="secondary">
                  {metadata.phone || "(555) 123-4567"}
                </Text>
              </div>
            </Space>

            <Space align="start">
              <MailOutlined style={{ fontSize: 22, color: "#faad14" }} />
              <div>
                <Text strong>Email</Text>
                <br />
                <Text type="secondary">
                  {metadata.email || "noya.design.renovation@gmail.com"}
                </Text>
              </div>
            </Space>

            <Space align="start">
              <EnvironmentOutlined style={{ fontSize: 22, color: "#faad14" }} />
              <div>
                <Text strong>Showroom</Text>
                <br />
                <Text type="secondary">
                  {metadata.address || "123 Design District, New York"}
                </Text>
              </div>
            </Space>

            <Space align="start">
              <ClockCircleOutlined style={{ fontSize: 22, color: "#faad14" }} />
              <div>
                <Text strong>Business Hours</Text>
                <br />
                <Text type="secondary">
                  {metadata.hours || "Monday - Friday: 9AM - 6PM"}
                </Text>
              </div>
            </Space>
          </Space>
        </Col>

        {/* RIGHT SIDE FORM */}
        <Col xs={24} lg={12}>
          <Card>
            <Title level={3}>Free Consultation Request</Title>

            {isSubmitted && (
              <Alert
                title="Thank you! We received your request."
                type="success"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}

            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              style={{ marginTop: 20 }}
            >
              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please enter your name" },
                    ]}
                  >
                    <Input placeholder="John Doe" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Invalid email format" },
                    ]}
                  >
                    <Input placeholder="john@example.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Phone Number" name="phone">
                    <Input placeholder="012-3456789" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Project Type"
                    name="projectType"
                    rules={[
                      {
                        required: true,
                        message: "Select project type",
                      },
                    ]}
                  >
                    <Select placeholder="Select project type">
                      {projectTypes.map((option) => (
                        <Select.Option key={option} value={option}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={20}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Budget" name="budget">
                    <Select placeholder="Select budget">
                      {budgets.map((option) => (
                        <Select.Option key={option} value={option}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Timeline" name="timeline">
                    <Select placeholder="Select timeline">
                      {timelines.map((option) => (
                        <Select.Option key={option} value={option}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Project Details"
                name="message"
                rules={[
                  {
                    required: true,
                    message: "Please describe your project",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Tell us about your project..."
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                block
                size="large"
              >
                Request Free Consultation
              </Button>
            </Form>

            <Divider />

            <Text type="secondary" style={{ fontSize: 12 }}>
              * Required fields. We respect your privacy and will never share
              your information.
            </Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Contact;
